import React, { useLayoutEffect, useRef } from "react";
import { MIN_FIT_SCALE } from "./canvasModel";

// Shrink-to-fit container for one canvas element.
//
// The element's box is fixed (that is the whole point of a free-form ticket),
// so content that is too big for it — a long ornaments list, long terms, a long
// customer name — is scaled DOWN inside the box rather than pushing anything
// else on the page. Floor of MIN_FIT_SCALE; past that it clips and the editor
// flags the box (data-overflow).
//
// Scaling is a transform with a compensating width, so text re-wraps at the
// smaller size and fills the box's full width. The chosen scale is written to
// the DOM inline, which is what lets the print window (a static clone, no
// React) print exactly what the preview measured.
//
// Both boxes are measured with getBoundingClientRect, so any zoom/transform on
// an ancestor (the studio's preview scale) cancels out of the ratio.
// Last resort: below the preferred floor (MIN_FIT_SCALE) content still
// shrinks this far before anything is clipped.
const HARD_FLOOR = 0.25;

const FitBox = ({ children, vAlign = "top", minScale = MIN_FIT_SCALE, contentKey }) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return undefined;
    let frame = 0;
    let busy = false;

    const align =
      vAlign === "middle" ? "center" : vAlign === "bottom" ? "flex-end" : "flex-start";
    const apply = (s) => {
      inner.style.transform = s < 1 ? `scale(${s})` : "";
      // 1px short of the frame once scaled: sub-pixel rounding would otherwise
      // shave the right-hand border of a boxed block (it sits on the very edge).
      inner.style.width = s < 1 ? `calc(${100 / s}% - ${1 / s}px)` : "100%";
      // A scaled box keeps its unscaled LAYOUT height, which is taller than the
      // frame — centring that would push its top out of view. Once scaled it
      // fills the frame anyway, so pin it to the top.
      outer.style.justifyContent = s < 1 ? "flex-start" : align;
    };
    // Height is measured on screen; width as "nothing wider than its own box"
    // (a table that cannot wrap), since the box width is ours to set.
    const fits = () => {
      const o = outer.getBoundingClientRect();
      const r = inner.getBoundingClientRect();
      return r.height <= o.height - 0.5 && inner.scrollWidth <= inner.clientWidth + 0.5;
    };
    const fit = () => {
      if (busy) return;
      busy = true;
      apply(1);
      let s = 1;
      if (!fits()) {
        // Binary search: re-wrapping at a wider layout width means height is
        // not linear in scale, so a single ratio would over-shrink. Search
        // above the preferred floor first; if even that is too big, keep going
        // down rather than clip — a clipped block loses its bottom border and
        // whatever text was under it, on paper too.
        let lo = minScale;
        let hi = 1;
        apply(lo);
        if (!fits()) {
          hi = lo;
          lo = HARD_FLOOR;
        }
        for (let i = 0; i < 8; i++) {
          const mid = (lo + hi) / 2;
          apply(mid);
          if (fits()) lo = mid;
          else hi = mid;
        }
        s = lo;
        apply(s);
      }
      outer.dataset.overflow = !fits() ? "1" : "";
      outer.dataset.scale = String(Math.round(s * 100) / 100);
      busy = false;
    };

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    };
    fit();
    const ro = new ResizeObserver(schedule);
    ro.observe(outer);
    // Images load late and change height — refit when any finishes.
    const imgs = Array.from(inner.querySelectorAll("img"));
    imgs.forEach((img) => img.addEventListener("load", schedule));
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      imgs.forEach((img) => img.removeEventListener("load", schedule));
    };
  });
  // ^ no deps on purpose: content can change through any prop (data, style,
  // font size) and fitting is cheap. contentKey is only a hint for readers.
  void contentKey;

  return (
    <div
      ref={outerRef}
      className="tc-fit"
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent:
          vAlign === "middle" ? "center" : vAlign === "bottom" ? "flex-end" : "flex-start",
      }}
    >
      <div
        ref={innerRef}
        className="tc-fit-inner"
        style={{ width: "100%", transformOrigin: "top left", flexShrink: 0 }}
      >
        {children}
      </div>
    </div>
  );
};

export default FitBox;
